import React from 'react';
import { motion } from 'framer-motion';
import { Trash2, RotateCcw, AlertTriangle, UserMinus, Pin, Shield, Edit3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { canModerateSession } from '../utils/permissions';

export const ContentModerationActions = ({ onDelete, onPin, onMute, onEdit, isPinned, isMuted, sessionId, communityId, isAuthor = false, session = null }) => {
  const { user } = useAuth();
  const canModerate = canModerateSession(user, sessionId, communityId, session);

  if (!canModerate && !isAuthor) return null;

  return (
    <div className="flex items-center gap-1">
      {onEdit && (
        <button 
          onClick={onEdit}
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
          title="Edit Content"
        >
          <Edit3 size={14} />
        </button>
      )}
      {canModerate && (
        <>
          <button 
            onClick={onPin}
            className={`p-1.5 rounded-lg transition-colors ${isPinned ? 'text-brand-indigo bg-brand-indigo/10' : 'text-gray-400 hover:bg-gray-100'}`}
            title={isPinned ? "Unpin" : "Pin to Top"}
          >
            <Pin size={14} fill={isPinned ? "currentColor" : "none"} />
          </button>
          <button 
            onClick={onMute}
            className={`p-1.5 rounded-lg transition-colors ${isMuted ? 'text-orange-600 bg-orange-50' : 'text-gray-400 hover:bg-gray-100'}`}
            title={isMuted ? "Unmute User" : "Mute User"}
          >
            <UserMinus size={14} fill={isMuted ? "currentColor" : "none"} />
          </button>
        </>
      )}
      <button 
        onClick={onDelete}
        className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
        title="Delete Content"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

export const SoftDeletePlaceholder = ({ onRestore, type = "content", sessionId, communityId, session = null }) => {
  const { user } = useAuth();
  
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="p-4 bg-gray-50 border border-dashed border-gray-200 rounded-2xl flex items-center justify-between"
    >
      <div className="flex items-center gap-2 text-gray-400">
        <AlertTriangle size={16} />
        <span className="text-xs font-medium italic">This {type} has been removed by a moderator.</span>
      </div>
      {canModerateSession(user, sessionId, communityId, session) && (
        <button 
          onClick={onRestore}
          className="flex items-center gap-1.5 text-xxs font-bold uppercase tracking-widest text-brand-indigo hover:text-brand-indigo/90 bg-white px-3 py-1.5 rounded-xl border border-brand-indigo/10 shadow-sm"
        >
          <RotateCcw size={12} />
          Restore
        </button>
      )}
    </motion.div>
  );
};

export const ModeratorLog = ({ logs }) => (
  <div className="bg-white rounded-3xl border border-gray-100 p-6">
    <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-4 uppercase tracking-widest text-xs">
      <Shield size={16} className="text-brand-indigo" />
      Moderation Logs
    </h3>
    <div className="space-y-3">
      {Array.isArray(logs) && logs.map((log, i) => (
        <div key={i} className="flex gap-3 text-xs">
          <span className="text-gray-400 font-bold whitespace-nowrap">{log.time}</span>
          <p className="text-gray-600">
            <span className="font-bold text-gray-900">{log.admin}</span> {log.action} <span className="font-medium text-brand-indigo italic">"{log.target_preview || log.target_type || log.target}"</span>
          </p>
        </div>
      ))}
    </div>
  </div>
);
